// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Key, Copy, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface AdminPasswordResetProps {
  userId?: string;
  userEmail?: string;
  userName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const AdminPasswordReset: React.FC<AdminPasswordResetProps> = ({
  userId: propUserId,
  userEmail: propUserEmail,
  userName: propUserName,
  open: propOpen,
  onOpenChange: propOnOpenChange
}) => {
  const [userId, setUserId] = useState(propUserId || '');
  const [isResetting, setIsResetting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(propOpen || false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  const isControlled = propOpen !== undefined && propOnOpenChange !== undefined;
  const dialogOpen = isControlled ? propOpen : isDialogOpen;
  const setDialogOpen = isControlled ? propOnOpenChange : setIsDialogOpen;

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setNewPassword(password);
    setConfirmPassword(password);
    setGeneratedPassword(password);
    setIsCopied(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setIsCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy password');
    }
  };

  const handleSendEmail = async () => {
    if (!propUserEmail?.trim()) {
      toast.error('User email is required');
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(propUserEmail.trim(), {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        throw error;
      }

      toast.success(`Password reset email sent to ${propUserEmail}`);
      setDialogOpen(false);
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send password reset email';
      toast.error(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const handleSetPassword = async () => {
    if (!propUserId?.trim()) {
      toast.error('User ID is required');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsResetting(true);

    try {
      const { data, error } = await invokeEdgeFunction('admin-password-management', {
        body: {
          action: 'set_password',
          userId: propUserId,
          password: newPassword
        }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to set password');
      }

      toast.success('Password set successfully! You can now share this password with the user.');
      setGeneratedPassword(newPassword);
      // Don't close dialog so admin can copy the password
    } catch (error) {
      console.error('Set password error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to set password';
      toast.error(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const content = (
    <Tabs defaultValue="email" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="email">Send Email</TabsTrigger>
        <TabsTrigger value="direct">Set Password</TabsTrigger>
      </TabsList>
      
      <TabsContent value="email" className="space-y-6 mt-4">
        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
          <AlertTriangle className="h-5 w-5" />
          <p className="text-sm font-medium">
            This will send a password reset email to the user. The link will expire in 1 hour.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            {propUserEmail ? (
              <>
                <Label>Sending reset email to:</Label>
                <p className="text-sm font-medium">
                  {propUserName ? `${propUserName} (${propUserEmail})` : propUserEmail}
                </p>
              </>
            ) : (
              <>
                <Label htmlFor="userId">User Email</Label>
                <Input
                  id="userId"
                  value={propUserId || userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter user email"
                  disabled={!!propUserId}
                />
              </>
            )}
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>How it works:</strong><br/>
              • The user will receive an email with a secure password reset link<br/>
              • They can click the link to set their own new password<br/>
              • The link expires in 1 hour for security
            </p>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outline"
            className="flex-1"
            disabled={isResetting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={isResetting || !propUserEmail}
            className="flex-1"
          >
            {isResetting ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="direct" className="space-y-6 mt-4">
        <div className="flex items-center gap-2 text-blue-600 bg-blue-50 p-3 rounded-lg">
          <Key className="h-5 w-5" />
          <p className="text-sm font-medium">
            Set a password directly for this user. You can generate a secure password or create your own.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>User:</Label>
            <p className="text-sm font-medium">
              {propUserName ? `${propUserName} (${propUserEmail})` : propUserEmail}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="newPassword">New Password</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generatePassword}
                disabled={isResetting}
              >
                Generate Secure Password
              </Button>
            </div>
            <Input
              id="newPassword"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter password (min 6 characters)"
              disabled={isResetting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="text"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              disabled={isResetting}
            />
          </div>

          {generatedPassword && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-900 mb-2">
                Password set successfully! Share this with the user:
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border border-green-300 text-sm font-mono">
                  {generatedPassword}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={copyToClipboard}
                  className="flex items-center gap-1"
                >
                  {isCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-900">
              <strong>Important:</strong><br/>
              • Make sure to copy and securely share this password with the user<br/>
              • The password will be set immediately in the auth system<br/>
              • User can login with this password right away
            </p>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            onClick={() => {
              setDialogOpen(false);
              setNewPassword('');
              setConfirmPassword('');
              setGeneratedPassword('');
            }}
            variant="outline"
            className="flex-1"
            disabled={isResetting}
          >
            {generatedPassword ? 'Done' : 'Cancel'}
          </Button>
          {!generatedPassword && (
            <Button
              onClick={handleSetPassword}
              disabled={isResetting || !newPassword || !confirmPassword}
              className="flex-1"
            >
              {isResetting ? 'Setting...' : 'Set Password'}
            </Button>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );

  if (isControlled) {
    return (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Send Password Reset Email
            </DialogTitle>
            <DialogDescription>
              Choose to send a password reset email or set a password directly for this user.
            </DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Send Password Reset Email
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

export default AdminPasswordReset;